import {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Alert, Button, Input, Table} from 'antd';
import {GET_COMPLY_CUBE_ADDRESSES} from '../resolvers';
import {
  buildAddressesInput,
  formatDate,
  unwrapListResponse,
  COMPLY_CUBE_ADDRESSES_KEY,
} from '../kycComplyCubeUtils';
import {
  ComplyCubeTablePagination,
  ComplyCubeTruncatedClientId,
  ComplyCubeUserCell,
} from './ComplyCubeTableShared';
import styles from '../kycDashboard.module.scss';

export function ComplyCubeAddressesTab({openClientDetail}) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchDraft, setSearchDraft] = useState('');
  const [clientIdDraft, setClientIdDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedClientId, setAppliedClientId] = useState('');

  const showReset =
    searchDraft.trim() !== '' ||
    clientIdDraft.trim() !== '' ||
    appliedSearch.trim() !== '' ||
    appliedClientId.trim() !== '';

  const handleApply = () => {
    setAppliedSearch(searchDraft);
    setAppliedClientId(clientIdDraft.trim());
    setPage(1);
  };

  const handleReset = () => {
    setSearchDraft('');
    setClientIdDraft('');
    setAppliedSearch('');
    setAppliedClientId('');
    setPage(1);
  };

  const variables = useMemo(
    () => ({
      input: buildAddressesInput(page, limit, {
        search: appliedSearch,
        clientId: appliedClientId,
        country: '',
      }),
    }),
    [page, limit, appliedSearch, appliedClientId],
  );

  const {data, loading} = useQuery(GET_COMPLY_CUBE_ADDRESSES, {
    variables,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const {error, rows, total} = unwrapListResponse(
    data,
    COMPLY_CUBE_ADDRESSES_KEY,
    'ResponseWithComplyCubeAddresses',
  );

  return (
    <>
      {error ? (
        <Alert
          type="error"
          message={error.message || 'Request failed'}
          showIcon
        />
      ) : null}
      <div className={styles.viewerFilters}>
        <Input
          allowClear
          placeholder="Search…"
          className={styles.viewerSearch}
          value={searchDraft}
          onChange={e => setSearchDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <Input
          allowClear
          placeholder="Client ID"
          className={styles.viewerFilterInput}
          value={clientIdDraft}
          onChange={e => setClientIdDraft(e.target.value)}
          onPressEnter={handleApply}
        />
        <div className={styles.viewerFilterActions}>
          <Button type="primary" onClick={handleApply}>
            Submit
          </Button>
          {showReset ? <Button onClick={handleReset}>Reset</Button> : null}
        </div>
      </div>
      <Table
        className="antd-table"
        loading={loading}
        pagination={false}
        rowKey={r => r.id || r._id}
        dataSource={rows}
        onRow={record => ({
          onClick: () => {
            const cid = record?.clientId;
            if (cid) openClientDetail(cid);
          },
          className: styles.clickableRow,
        })}
        columns={[
          {
            title: 'User',
            key: 'user',
            width: 228,
            render: (_, r) => <ComplyCubeUserCell user={r?.user} />,
          },
          {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            render: id => <ComplyCubeTruncatedClientId id={id} />,
          },
          {
            title: 'Street',
            dataIndex: 'line',
            key: 'line',
          },
          {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
          },
          {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
          },
          {
            title: 'Postal code',
            dataIndex: 'postalCode',
            key: 'postalCode',
          },
          {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
          },
          {
            title: 'Created at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: formatDate,
          },
        ]}
      />
      <ComplyCubeTablePagination
        page={page}
        limit={limit}
        total={total}
        rowCount={rows.length}
        loading={loading}
        onPageChange={(nextPage, nextLimit) => {
          setLimit(nextLimit);
          setPage(nextPage);
        }}
        itemName="addresses"
      />
    </>
  );
}
